<script setup lang="ts">
import { formatDateOnly, PaginationComponent } from '@metapic-apps/shared'
import { ref, watch } from 'vue'

import RemoveIcon from '@/assets/icons/remove.svg?component'
import SearchIcon from '@/assets/icons/search.svg?component'
import EmptyState from '@/components/EmptyState/EmptyState.vue'
import { debounce } from '@/lib/helpers'
import type { BlockListItemType } from '@/lib/types/blocklist.types'
import type { FilterType } from '@/lib/types/common.types'
import {
  BoxComponent,
  ButtonComponent,
  FlexComponent,
  ImageComponent,
  InputComponent,
  LoadingComponent,
  TypographyComponent,
} from '@/ui-library'

const showSearch = ref(false)
const props = defineProps<{ items: BlockListItemType[] | null; totalPages: number; isLoading: boolean }>()
const emits = defineEmits(['remove', 'update:filter'])
const filter = defineModel<FilterType>('filter', { required: true })
const handleSearchQuery = debounce((query: string) => emits('update:filter', { ...filter.value, page: 1, query }), 300)

watch(
  () => props.items,
  (items) => {
    if (items?.length) {
      showSearch.value = true
    }
  },
  { immediate: true },
)
</script>
<template>
  <BoxComponent w="100%">
    <BoxComponent v-if="showSearch" :w="280" :mb="24">
      <InputComponent placeholder="Search by ID or username" size="small" @input="handleSearchQuery">
        <template #prefix>
          <el-icon :size="18">
            <SearchIcon />
          </el-icon>
        </template>
      </InputComponent>
    </BoxComponent>
    <LoadingComponent v-if="isLoading" :size="64" />
    <template v-else-if="items?.length">
      <FlexComponent :gap="8" direction="column">
        <BoxComponent v-for="item in items" :key="item.id" :p="16" class="blocklist__item">
          <FlexComponent :gap="16" align="center">
            <template v-if="item.user">
              <BoxComponent w="25%" overflow-hidden>
                <FlexComponent align="center" :gap="8">
                  <ImageComponent
                    v-if="item.user.image"
                    :image="item.user.image"
                    :width="32"
                    :height="32"
                    :alt="item.user.username"
                    class="blocklist__item-avatar"
                  />
                  <TypographyComponent variant="title-large" text-truncate white-space="nowrap">
                    {{ item.user.username || item.user.email }}
                  </TypographyComponent>
                </FlexComponent>
              </BoxComponent>
              <BoxComponent w="15%">
                <TypographyComponent variant="label-medium" text-align="center">Creator ID</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ item.user.id }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="20%">
                <TypographyComponent variant="label-medium" text-align="center">Blocked since</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ formatDateOnly(item.createdAt) }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="15%">
                <TypographyComponent variant="label-medium" text-align="center">Link revenue</TypographyComponent>
                <TypographyComponent v-if="item.validateClicks" variant="title-large" text-align="center" color="var(--error)">
                  Stopped
                </TypographyComponent>
                <TypographyComponent v-else variant="title-large" text-align="center" color="var(--success)"> Working </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="20%">
                <TypographyComponent variant="label-medium" text-align="center">Blocked by</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ item.createdBy?.username || item.createdBy?.email || 'Deleted User' }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent ml="auto">
                <ButtonComponent variant="transparent" is-icon @click="() => emits('remove', item)">
                  <RemoveIcon />
                </ButtonComponent>
              </BoxComponent>
            </template>
            <template v-if="item.userTag">
              <BoxComponent w="25%" overflow-hidden>
                <TypographyComponent variant="title-large" text-truncate white-space="nowrap">
                  {{ item.userTag.name }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="15%">
                <TypographyComponent variant="label-medium" text-align="center">Tag ID</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ item.userTag.id }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="20%">
                <TypographyComponent variant="label-medium" text-align="center">Blocked since</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ formatDateOnly(item.createdAt) }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="15%">&nbsp;</BoxComponent>
              <BoxComponent w="20%">
                <TypographyComponent variant="label-medium" text-align="center">Blocked by</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ item.createdBy?.username || item.createdBy?.email || 'Deleted User' }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent ml="auto">
                <ButtonComponent variant="transparent" is-icon @click="() => emits('remove', item)">
                  <RemoveIcon />
                </ButtonComponent>
              </BoxComponent>
            </template>
            <template v-if="item.client">
              <BoxComponent w="25%" overflow-hidden>
                <TypographyComponent variant="title-large" text-truncate white-space="nowrap">
                  {{ item.client.name }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="15%">
                <TypographyComponent variant="label-medium" text-align="center">Client ID</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ item.client.id }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="20%">
                <TypographyComponent variant="label-medium" text-align="center">Blocked since</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ formatDateOnly(item.createdAt) }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent w="15%">&nbsp;</BoxComponent>
              <BoxComponent w="20%">
                <TypographyComponent variant="label-medium" text-align="center">Blocked by</TypographyComponent>
                <TypographyComponent variant="title-large" text-align="center">
                  {{ item.createdBy?.username || item.createdBy?.email || 'Deleted User' }}
                </TypographyComponent>
              </BoxComponent>
              <BoxComponent ml="auto">
                <ButtonComponent variant="transparent" is-icon @click="() => emits('remove', item)">
                  <RemoveIcon />
                </ButtonComponent>
              </BoxComponent>
            </template>
          </FlexComponent>
        </BoxComponent>
      </FlexComponent>
      <BoxComponent v-if="totalPages !== 0 && items && items?.length > 0" :pt="32" :pb="32">
        <PaginationComponent v-model:current-page="filter.page" :total-pages="totalPages" />
      </BoxComponent>
    </template>
    <EmptyState v-else :is-search-state="!!filter?.query" empty-state-msg="No creator blocked yet" />
  </BoxComponent>
</template>
<style lang="scss" scoped>
.blocklist__item {
  border-radius: 16px;
  border: 1px solid rgba(34, 34, 34, 0.08);
  background: var(--inverted);

  &:hover {
    background: var(--secondary-16);
  }
}

.blocklist__item-avatar {
  width: 32px;
  height: 32px;
  border-radius: 64px;
}
</style>
